/*
 * Root router for the Create React App frontend.
 *
 * The route map keeps the student chat flow and faculty tooling distinct so
 * navigation mirrors the backend information architecture.
 */
import React from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import ChatBotUI from './components/ChatBotUI';
import HelpPage from './components/HelpPage';
import ProfilePage from "./components/ProfilePage";
import FeedbackPage from "./components/FeedbackPage";
import ManagerDashboard from './components/ManagerDashboard';
import AddCourse from './components/AddCourse';
import FacultyInsights from './components/FacultyInsights';
import SessionReviewPage from './components/SessionReviewPage';
import HistoryPage from './components/HistoryPage';
import { SessionProvider } from './context/SessionContext';

function AuthenticatedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ChatBotUI />} />
      <Route path="/dashboard" element={<ManagerDashboard />} />
      <Route path="/faculty/courses" element={<ManagerDashboard />} />
      <Route path="/faculty/uploads" element={<AddCourse />} />
      <Route path="/faculty/insights" element={<FacultyInsights />} />
      <Route path="/faculty/review" element={<SessionReviewPage />} />
      <Route path="/faculty/history" element={<SessionReviewPage />} />
      <Route path="/insights" element={<FacultyInsights />} />
      <Route path="/addcourse" element={<ManagerDashboard />} />
      <Route path="/history" element={<HistoryPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/feedback" element={<FeedbackPage/>} />
    </Routes>
  );
}

function App() {
  const [authChecked, setAuthChecked] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const isLocalDevHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

    async function ensureLocalDevSession() {
      const response = await fetch("/api/v1/login", {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to bootstrap local dev session");
      }
    }

    async function verifySession() {
      try {
        let response = await fetch("/api/v1/get-user-primary-attribute", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok && isLocalDevHost) {
          await ensureLocalDevSession();
          response = await fetch("/api/v1/get-user-primary-attribute", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          });
        }

        if (!response.ok) {
          window.location.href = "/api/v1/login";
          return;
        }

        const data = await response.json().catch(() => null);
        const hasRole = Boolean(data?.primary_attribute || data?.app_role);

        if (!hasRole) {
          if (isLocalDevHost) {
            await ensureLocalDevSession();
            const retryResponse = await fetch("/api/v1/get-user-primary-attribute", {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            });
            const retryData = await retryResponse.json().catch(() => null);
            const hasRetryRole = Boolean(retryData?.primary_attribute || retryData?.app_role);

            if (retryResponse.ok && hasRetryRole) {
              if (!cancelled) {
                setAuthChecked(true);
              }
              return;
            }
          }

          window.location.href = "/api/v1/login";
          return;
        }

        if (!cancelled) {
          setAuthChecked(true);
        }
      } catch (_error) {
        if (isLocalDevHost) {
          try {
            await ensureLocalDevSession();
            const retryResponse = await fetch("/api/v1/get-user-primary-attribute", {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            });
            const retryData = await retryResponse.json().catch(() => null);

            if (retryResponse.ok && (retryData?.primary_attribute || retryData?.app_role)) {
              if (!cancelled) {
                setAuthChecked(true);
              }
              return;
            }
          } catch (_retryError) {
            // Fall through to the normal login redirect if local bootstrap fails.
          }
        }

        window.location.href = "/api/v1/login";
      }
    }

    verifySession();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SessionProvider>
      <Router>
      <div className="App">
        {authChecked ? (
          <AuthenticatedRoutes />
        ) : (
          <main
            style={{
              minHeight: "100vh",
              display: "grid",
              placeItems: "center",
              padding: "2rem",
            }}
          >
            <div>Checking sign-in…</div>
          </main>
        )}
      </div>
      </Router>
    </SessionProvider>
  );
}

export default App;
